// @ts-nocheck
import React, {
  useState,
  useCallback,
  useMemo,
  useEffect,
  useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';

import FullPageCard from '@/components/organism/Cards/FullPageCard';
import PageToolbar from '@/components/organism/Cards/PageToolbar';
import { DataTable } from '@/components/organism/Table';
import type { SortState } from '@/components/organism/Table/types';
import SegmentedControl from '@/components/elements/SegmentedControl';
import GroupDropdown from '@/components/organism/GroupDropdown';

import {
  fetchCoolerList,
  triggerCoolerAction,
  resetCoolerAction,
} from '@/store/slices/smartCooler.slice';
import { RootState } from '@/store/root.reducer';
import {
  pagination as PAGINATION_INITIAL_STATE,
  ROW_PER_PAGE_OPTION,
} from '@/constant/global/global.constant';
import { getNavItemIcon, NAV_ITEMS } from '@/constant/layout/layout.constant';
import { tid } from '@/utils/testId';
import { SMART_COOLER_TEST_IDS as TID } from '@/constant/testIds/smartCooler.testIds';
import { useToast } from '@/components/organism/Toast/ToastProvider';

import {
  SMART_COOLER_PROPS,
  SMART_COOLER_STATUS,
  SMART_COOLER_STATUS_VALUES,
  FetchLogRange,
  RANGE_DAYS_MAP,
  VIEW_SC_STRINGS,
} from '@/constant/smartCooler/smartCooler.constant';
import { ISmartCooler } from './smartCooler.interface';
import FetchLogModal from './FetchLogModal';
import { buildColumns } from './smartCooler.columns';

const SmartCooler = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const {
    actionLoading,
    actionSuccess,
    actionError,
    actionMessage,
    actionData,
    data,
    listLoading,
    pagination: paginationResponse,
  } = useSelector((state: RootState) => state.smartCooler);
  const toast = useToast();

  const [selectedStatus, setSelectedStatus] = useState<string>(
    SMART_COOLER_STATUS_VALUES.CONNECTED,
  );
  const [sorting, setSorting] = useState<SortState>({
    orderBy: 'updatedOn',
    order: 'desc',
  });
  const [pagination, setPagination] = useState(PAGINATION_INITIAL_STATE);
  const [isCopy, setIsCopy] = useState('');
  const hasLoaded = useRef(false);
  const copyTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const [selection, setSelection] = useState<{
    provider: any | null;
    operator: any | null;
    market: any | null;
    device: any | null;
  }>({ provider: null, operator: null, market: null, device: null });

  const [fetchLogModal, setFetchLogModal] = useState<{
    open: boolean;
    item: ISmartCooler | null;
  }>({ open: false, item: null });

  const openFetchLogModal = (item: ISmartCooler) =>
    setFetchLogModal({ open: true, item });
  const closeFetchLogModal = () =>
    setFetchLogModal({ open: false, item: null });

  const totalPages = useMemo(
    () => Math.ceil((paginationResponse?.totalRecords || 0) / pagination.limit),
    [paginationResponse?.totalRecords, pagination.limit],
  );

  const resetPagination = () => setPagination((p) => ({ ...p, page: 1 }));

  const handleSelectionChange = (vals: any) => {
    setSelection(vals);
    resetPagination();
  };
  const handleStatusChange = (value: string) => {
    setSelectedStatus(value);
    resetPagination();
  };
  const handlePreviousClick = (page: number) => {
    if (page > 1) setPagination((p) => ({ ...p, page: page - 1 }));
  };
  const handleNextClick = (page: number) => {
    if (page < totalPages) setPagination((p) => ({ ...p, page: page + 1 }));
  };
  const handleRowsPerPageChange = useCallback(
    (limit: number) => {
      setPagination((p) => ({ ...p, limit }));
      if (totalPages < pagination.page)
        setPagination((p) => ({ ...p, page: totalPages }));
    },
    [pagination.page, totalPages],
  );

  const onCopyClick = (key: string) => {
    setIsCopy(key);
    if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    copyTimeoutRef.current = setTimeout(() => setIsCopy(''), 2000);
  };

  useEffect(() => {
    if (!hasLoaded.current && Array.isArray(data) && data.length > 0)
      hasLoaded.current = true;
  }, [data]);

  useEffect(() => {
    dispatch(
      fetchCoolerList({
        page: pagination.page,
        limit: pagination.limit,
        orderBy: sorting.orderBy,
        order: sorting.order,
        ...(selectedStatus !== 'ALL' ? { status: selectedStatus } : {}),
        ...(selection?.operator?.sourceId && selection.operator.sourceId !== '1'
          ? { operatorId: selection.operator.sourceId }
          : {}),
      }),
    );
  }, [
    pagination.page,
    pagination.limit,
    sorting.orderBy,
    sorting.order,
    selectedStatus,
    selection,
    dispatch,
  ]);

  useEffect(() => {
    if (actionSuccess) {
      toast?.pushSuccess(
        actionMessage || VIEW_SC_STRINGS.COOLER_ACTION_SUCCESS_MSG,
      );
    }
    if (actionError) {
      toast?.pushError(actionError);
    }
  }, [actionSuccess, actionError]);

  useEffect(() => {
    return () => {
      if (copyTimeoutRef.current) clearTimeout(copyTimeoutRef.current);
    };
  }, []);

  const handleFetchLogSubmit = (item: ISmartCooler, range: FetchLogRange) => {
    dispatch(
      triggerCoolerAction({
        coolerId: item.coolerId,
        type: 'UPLOAD_LOGS',
        options: { days: RANGE_DAYS_MAP[range] },
      }),
    );
  };

  const columns = useMemo(
    () =>
      buildColumns(isCopy, onCopyClick, openFetchLogModal, (item) =>
        navigate(`/smartcoolers/${item.coolerId}`, { state: { item } }),
      ),
    [isCopy],
  );

  return (
    <>
      <FullPageCard
        testId={tid(TID.PAGE)}
        header={
          <PageToolbar
            title={SMART_COOLER_PROPS.HEADING}
            subtitle={SMART_COOLER_PROPS.SUB_HEADING}
            icon={getNavItemIcon(NAV_ITEMS.SMART_COOLERS)}
            count={paginationResponse?.totalRecords}
            filtersLeft={
              <GroupDropdown
                onSelectionChange={handleSelectionChange}
                showOperator
                showProvider={false}
                showLocation={false}
                dataTestId={tid(TID.FILTER_DROPDOWN)}
              />
            }
            filtersRight={
              <SegmentedControl
                testId={tid(TID.STATUS_FILTER)}
                size="lg"
                shape="square"
                variant="gradient"
                options={SMART_COOLER_STATUS.map((s) => ({
                  value: s.id,
                  label: s.name,
                }))}
                value={selectedStatus}
                onChange={handleStatusChange}
              />
            }
          />
        }
      >
        <DataTable
          data-testid={tid(TID.TABLE)}
          data={data}
          columns={columns}
          loading={listLoading}
          skeletonRows={15}
          onSort={(orderBy, order) =>
            setSorting({ orderBy: orderBy as string, order })
          }
          sortState={sorting}
          rowKey={(item: any) => item?.coolerId ?? ''}
          rowTestId={(item: any, idx: number) =>
            TID.TABLE_ROW(`${idx}`)
          }
          onRowClick={(item) =>
            navigate(`/smartcoolers/${item.coolerId}`, { state: { item } })
          }
          pagination={
            !isEmpty(data)
              ? {
                state: pagination,
                totalItem: paginationResponse?.totalRecords,
                rowsPerPageOptions: ROW_PER_PAGE_OPTION,
                onRowsPerPageChange: handleRowsPerPageChange,
                onPreviousClick: handlePreviousClick,
                onNextClick: handleNextClick,
              }
              : undefined
          }
        />
      </FullPageCard>

      <FetchLogModal
        open={fetchLogModal.open}
        item={fetchLogModal.item}
        onClose={closeFetchLogModal}
        onSubmit={handleFetchLogSubmit}
        isLoading={actionLoading}
        isSuccess={actionSuccess}
        isError={!!actionError}
        actionMessage={actionMessage}
        actionData={actionData}
        actionError={actionError}
        onDoneClose={() => {
          dispatch(resetCoolerAction());
          closeFetchLogModal();
        }}
      />
    </>
  );
};

export default SmartCooler;
